import { memo } from "react";
import LoadingButton from "@mui/lab/LoadingButton";
import Divider from "@mui/material/Divider";
import VisibilityIcon from '@mui/icons-material/Visibility';
import { Psp } from "consts/transactions";
import { when } from "util/support";
import { getInNotifications, getOutNotifications } from "features/transactions/api";
import { withSx } from "ui/atoms/Modal";
import Tabs from "ui/atoms/Tabs";
import { TriggerableDialog } from "ui/molecules/Dialog";
import { TabLayout } from "ui/widgets/StatefulTabs";
import GeneralInfo from "./GeneralInfo";
import Repository from "./Repository";
import History, { useHistory } from "./History";
import Request from "./Request";
import Response from "./Response";
import SystemDecision from "./SystemDecision";
import Notifications, { useNotifications } from "./Notifications";
import BankingSDKLogging, { useBankingSDKLogging } from "./BankingSDKLogging";
import { CurrentTransactionStateTab } from "./types";
import { useTransactionContext } from "../TransactionProvider";

const CurrentTransactionState = () => {
    const transaction = useTransactionContext();

    const historyRepository = useHistory();
    const inNotificationsRepository = useNotifications(getInNotifications);
    const outNotificationsRepository = useNotifications(getOutNotifications);
    const bankingSDKLoggingRepository = useBankingSDKLogging();

    const reset = () => [
        historyRepository,
        inNotificationsRepository,
        outNotificationsRepository,
        bankingSDKLoggingRepository
    ].forEach(repository => repository.reset());

    const props = {
        sx: {
            height: 'calc(100vh - 358px)',
            display: 'grid'
        }
    };

    return (
        <TriggerableDialog
            TriggerComponent={({ onClick }) => (
                <LoadingButton
                    variant='outlined'
                    onClick={onClick}
                    endIcon={(
                        <VisibilityIcon />
                    )}
                >
                    View Transaction Details
                </LoadingButton>
            )}
            ModalProps={{
                titleSlot: 'Current Transaction State',
                onClose: reset,
                DialogProps: withSx({
                    maxWidth: 'lg'
                })
            }}
        >
            <GeneralInfo />
            <Divider sx={{
                marginTop: 3,

            }} />
            <Tabs
                model={[
                    {
                        key: CurrentTransactionStateTab.Request,
                        label: 'Request',
                        component: (
                            <TabLayout
                                {...props}
                            >
                                <Request />
                            </TabLayout>
                        )
                    },
                    {
                        key: CurrentTransactionStateTab.Response,
                        label: 'Response',
                        component: (
                            <TabLayout
                                {...props}
                            >
                                <Response />
                            </TabLayout>
                        )
                    },
                    {
                        key: CurrentTransactionStateTab.SystemDecision,
                        label: 'System Decision',
                        component: (
                            <TabLayout
                                {...props}
                            >
                                <SystemDecision />
                            </TabLayout>
                        )
                    },
                    {
                        key: CurrentTransactionStateTab.TransactionHistory,
                        label: 'Transaction History',
                        component: (
                            <Repository
                                repository={historyRepository}
                            >
                                <History
                                    sx={{
                                        ...props.sx,
                                        width: '100%'
                                    }}
                                />
                            </Repository>
                        )
                    },
                    {
                        key: CurrentTransactionStateTab.InNotifications,
                        label: 'In Notifications',
                        component: (
                            <Repository
                                sx={{
                                    ...props.sx,
                                    placeContent: when(
                                        inNotificationsRepository.isRepositoryLoading,
                                        'center',
                                        'initial'
                                    )
                                }}
                                repository={inNotificationsRepository}
                                isLoading={inNotificationsRepository.isRepositoryLoading}
                                isError={inNotificationsRepository.isRepositoryError}
                                isEmpty={inNotificationsRepository.isRepositoryEmpty}
                            >
                                <Notifications />
                            </Repository>
                        )
                    },
                    {
                        key: CurrentTransactionStateTab.OutNotifications,
                        label: 'Out Notifications',
                        component: (
                            <Repository
                                sx={{
                                    ...props.sx,
                                    placeContent: when(
                                        outNotificationsRepository.isRepositoryLoading,
                                        'center',
                                        'initial'
                                    )
                                }}
                                repository={outNotificationsRepository}
                                isLoading={outNotificationsRepository.isRepositoryLoading}
                                isError={outNotificationsRepository.isRepositoryError}
                                isEmpty={outNotificationsRepository.isRepositoryEmpty}
                            >
                                <Notifications />
                            </Repository>
                        )
                    },
                    {
                        key: CurrentTransactionStateTab.BankingSdkLogging,
                        label: 'Banking SDK Logging',
                        component: (
                            <Repository
                                repository={bankingSDKLoggingRepository}
                            >
                                <BankingSDKLogging
                                    sx={{
                                        ...props.sx,
                                        width: '100%'
                                    }}
                                />
                            </Repository>
                        )
                    }
                ]
                    .filter(({ key }) => when(
                        Object.is(key, CurrentTransactionStateTab.BankingSdkLogging),
                        () => Object.is(transaction.PSP, Psp.BankingSdk),
                        () => true
                    ))}
                TabPanelProps={{
                    sx: {
                        p: 0
                    }
                }}
                TabListContainerProps={{
                    borderBottom: 1,
                    borderColor: 'divider'
                }}
                TabListProps={{
                    'aria-label': 'current transaction state',
                    variant: 'scrollable',
                    scrollButtons: 'auto'
                }}
            />
        </TriggerableDialog>
    );
};

export default memo(CurrentTransactionState);
